import Button from '@jetbrains/ring-ui/components/button/button'
import {Grid, Row, Col} from '@jetbrains/ring-ui/components/grid/grid'
import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import differenceInSeconds from 'date-fns/differenceInSeconds'
import parseISO from 'date-fns/parseISO'
import * as React from 'react'

import useDocumentTitle from '../../../../hooks/useDocumentTitle'
import {useIsAutodetectTimeZone} from '../../../../hooks/useIsAutodetectTimeZone'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import {getSingleBuildArg} from '../../../../rest/builds'
import {getBuildLocator} from '../../../../rest/locators'
import {restApi} from '../../../../services/rest'
import type {BuildTypeId, BuildId} from '../../../../types'
import {displayDate, formatDuration} from '../../../../utils/dateTimeUtils'
import BuildAgentLink from '../../../common/AgentLink/BuildAgentLink.container'
import BuildBranch from '../../../common/BranchLabel/BuildBranchLabel.container'
import BuildNumberContainer from '../../../common/BuildNumber/BuildNumber.container'
import BuildPath from '../../../common/BuildPath/BuildPath.container'
import BuildStatus from '../../../common/BuildStatus/BuildStatus.container'
import BuildTriggeredBy from '../../../common/BuildTriggeredBy/BuildTriggeredBy.container'
import IconButton from '../../../common/IconButton/IconButton'
import Loader from '../../../common/Loader/Loader'
import Plugin from '../../../common/Plugin/Plugin'
import CompareBuildDialog from '../CompareBuildDialog/CompareBuildDialog.container'

import BuildArtifactsSize from './BuildArtifactsSize/BuildArtifactsSize'

import styles from './CompareBuildsHeader.css'

type Props = {
  sourceId: BuildId
  compareWithId: BuildId | null | undefined
  hasAnyBranch: boolean
  sourceBuildLoaded: boolean
  sourceBuildStarted: string | null | undefined
  sourceBuildFinished: string | null | undefined
  sourceBuildNumber: string | null | undefined
  sourceHasAgent: boolean | null | undefined
  sourceBuildTypeId: BuildTypeId | null | undefined
  compareBuildLoaded: boolean
  compareBuildStarted: string | null | undefined
  compareBuildFinished: string | null | undefined
  compareBuildNumber: string | null | undefined
  compareHasAgent: boolean | null | undefined
  compareBuildTypeId: BuildTypeId | null | undefined
  onCompareClick: () => unknown
  onSourceClick: () => unknown
}

const getTitle = (
  sourceBuildNumber: string | null | undefined,
  compareBuildNumber: string | null | undefined,
): string => {
  let title = 'Compare Builds'

  if (sourceBuildNumber != null) {
    title += ` #${sourceBuildNumber}`

    if (compareBuildNumber != null) {
      title += ` with #${compareBuildNumber}`
    }
  }

  title += ' — TeamCity'
  return title
}

function CompareBuildsHeader(props: Props): React.ReactNode {
  const {
    sourceId,
    compareWithId,
    hasAnyBranch,
    sourceBuildLoaded,
    sourceBuildStarted,
    sourceBuildFinished,
    sourceBuildNumber,
    sourceHasAgent,
    sourceBuildTypeId,
    compareBuildLoaded,
    compareBuildStarted,
    compareBuildFinished,
    compareBuildNumber,
    compareHasAgent,
    compareBuildTypeId,
    onCompareClick,
    onSourceClick,
  } = props
  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  restApi.endpoints.getBuildNormalized.useQuerySubscription(
    getSingleBuildArg(getBuildLocator(sourceId)),
  )
  restApi.endpoints.getBuildNormalized.useQuerySubscription(
    compareWithId != null ? getSingleBuildArg(getBuildLocator(compareWithId)) : skipToken,
  )
  const loading = compareWithId != null && !compareBuildLoaded
  const compareButtonClasses = classNames({
    [styles.invisible]: loading,
  })
  useDocumentTitle(getTitle(sourceBuildNumber, compareBuildNumber))
  return (
    <div className={styles.compareBuildsHeader}>
      <CompareBuildDialog buildId={sourceId} compareWithId={compareWithId} />
      {compareWithId != null && (
        <CompareBuildDialog buildId={compareWithId} compareWithId={sourceId} invertBuildsInLinks />
      )}
      <H1>{'Compare Builds'}</H1>
      <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
      {sourceBuildLoaded ? (
        <Grid className={styles.grid}>
          {sourceBuildTypeId != null && (
            <Row className={styles.row}>
              <Col xs={1} className={styles.column}>
                {'Path'}
              </Col>
              {/* eslint-disable-next-line @typescript-eslint/no-magic-numbers */}
              <Col xs={sourceBuildTypeId === compareBuildTypeId ? 11 : 5} className={styles.column}>
                <BuildPath
                  buildId={sourceId}
                  leafBuildTypeId={sourceBuildTypeId}
                  className={styles.buildPath}
                />
              </Col>
              {sourceBuildTypeId !== compareBuildTypeId && (
                <Col xs={6} className={styles.column}>
                  {compareBuildLoaded && (
                    <BuildPath
                      buildId={sourceId}
                      leafBuildTypeId={compareBuildTypeId}
                      className={styles.buildPath}
                    />
                  )}
                </Col>
              )}
            </Row>
          )}
          <Row className={styles.row}>
            <Col xs={1} className={styles.column}>
              {'Build'}
            </Col>
            <Col xs={5} className={styles.column}>
              <BuildNumberContainer buildId={sourceId} hideStar className={styles.buildNumber} />
              <IconButton
                icon="pencil"
                onClick={onSourceClick}
                title="Choose another build"
                className={classNames(styles.editIcon, {
                  [styles.invisible]: compareWithId == null,
                })}
              />
            </Col>
            <Col xs={6} className={styles.column}>
              {compareBuildLoaded && compareWithId != null && (
                <BuildNumberContainer
                  buildId={compareWithId}
                  hideStar
                  className={styles.buildNumber}
                />
              )}
              {loading && <LoaderInline>{'Loading...'}</LoaderInline>}
              {compareWithId != null ? (
                <IconButton
                  icon="pencil"
                  onClick={onCompareClick}
                  className={classNames(styles.editIcon, compareButtonClasses)}
                  title="Choose another build"
                />
              ) : (
                <Button onClick={onCompareClick} className={compareButtonClasses}>
                  {'Select build to compare'}
                </Button>
              )}
            </Col>
          </Row>
          <Row className={styles.row}>
            <Col xs={1} className={styles.column}>
              {'Status'}
            </Col>
            <Col xs={5} className={styles.column}>
              <BuildStatus buildId={sourceId} />
            </Col>
            <Col xs={6} className={styles.column}>
              {compareBuildLoaded && compareWithId != null && (
                <BuildStatus buildId={compareWithId} />
              )}
            </Col>
          </Row>
          {hasAnyBranch && (
            <Row className={styles.row}>
              <Col xs={1} className={styles.column}>
                {'Branch'}
              </Col>
              <Col xs={5} className={styles.column}>
                <BuildBranch buildId={sourceId} />
              </Col>
              <Col xs={6} className={styles.column}>
                {compareBuildLoaded && <BuildBranch buildId={compareWithId} />}
              </Col>
            </Row>
          )}
          <Row className={styles.row}>
            <Col xs={1} className={styles.column}>
              {'Duration'}
            </Col>
            <Col xs={5} className={styles.column}>
              {sourceBuildFinished != null && sourceBuildStarted != null
                ? formatDuration(
                    differenceInSeconds(
                      parseISO(sourceBuildFinished),
                      parseISO(sourceBuildStarted),
                    ),
                    {
                      alwaysShowSeconds: true,
                    },
                  )
                : 'Running'}
              {sourceBuildStarted != null &&
                `, started at ${displayDate(sourceBuildStarted, isAutodetectTimeZone, true)}`}
            </Col>
            <Col xs={6} className={styles.column}>
              {compareBuildLoaded && (
                <>
                  {compareBuildFinished != null && compareBuildStarted != null
                    ? formatDuration(
                        differenceInSeconds(
                          parseISO(compareBuildFinished),
                          parseISO(compareBuildStarted),
                        ),
                        {
                          alwaysShowSeconds: true,
                        },
                      )
                    : 'Running'}
                  {compareBuildStarted != null &&
                    `, started at ${displayDate(compareBuildStarted, isAutodetectTimeZone, true)}`}
                </>
              )}
            </Col>
          </Row>
          <Row className={styles.row}>
            <Col xs={1} className={styles.column}>
              {'Artifacts'}
            </Col>
            <Col xs={5} className={styles.column}>
              <BuildArtifactsSize buildId={sourceId} />
            </Col>
            <Col xs={6} className={styles.column}>
              {compareBuildLoaded && compareWithId != null && (
                <BuildArtifactsSize buildId={compareWithId} />
              )}
            </Col>
          </Row>
          {(sourceHasAgent === true || compareHasAgent === true) && (
            <Row className={styles.row}>
              <Col xs={1} className={styles.column}>
                {'Agent'}
              </Col>
              <Col xs={5} className={styles.column}>
                <BuildAgentLink buildId={sourceId} secondary={false} />
              </Col>
              <Col xs={6} className={styles.column}>
                {compareWithId != null && compareBuildLoaded && (
                  <BuildAgentLink buildId={compareWithId} secondary={false} />
                )}
              </Col>
            </Row>
          )}
          <Row className={styles.row}>
            <Col xs={6} className={styles.column}>
              <BuildTriggeredBy buildId={sourceId} inline={false} />
            </Col>
            <Col xs={6} className={styles.column}>
              {compareBuildLoaded && <BuildTriggeredBy buildId={compareWithId} inline={false} />}
            </Col>
          </Row>
        </Grid>
      ) : (
        <Loader />
      )}
    </div>
  )
}

export default CompareBuildsHeader
